import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircularProgress, Typography, Alert } from '@mui/material';
import { IntegrationService } from '../../services/api/integration';

// Create singleton instance outside component to prevent multiple instances
const integrationService = new IntegrationService();

// Track if an API call is in progress globally
let apiCallInProgress = false;

const OAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(true);
  const [callbackExecuted, setCallbackExecuted] = useState(false);

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (callbackExecuted) {
      console.log('[OAuthCallback] Already executed, skipping... (StrictMode protection)');
      return;
    }
    
    const handleCallback = async () => {
      // Generate unique request ID for debugging
      const requestId = Math.random().toString(36).substring(7);
      console.log(`[OAuthCallback-${requestId}] Starting handleCallback execution...`);
      setCallbackExecuted(true);
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      const provider = window.location.pathname.split('/').pop(); // Extract provider from URL

      // Debug logging
      console.log(`[OAuthCallback-${requestId}] OAuth Callback Debug:`, {
        pathname: window.location.pathname,
        pathSegments: window.location.pathname.split('/'),
        provider,
        hasCode: !!code,
        hasState: !!state,
        stateValue: state,
        codeLength: code?.length,
        error,
        errorDescription,
        fullUrl: window.location.href
      });

      if (error) {
        setError(`OAuth Error: ${errorDescription || error}`);
        setProcessing(false);
        return;
      }

      if (!code || !state || !provider) {
        const missingParams = [];
        if (!code) missingParams.push('code');
        if (!state) missingParams.push('state');
        if (!provider) missingParams.push('provider');
        setError(`Missing required parameters: ${missingParams.join(', ')}\nURL: ${window.location.href}`);
        setProcessing(false);
        return;
      }

      try {
        // Check if API call is already in progress
        if (apiCallInProgress) {
          console.log(`[OAuthCallback-${requestId}] API call already in progress, skipping...`);
          return;
        }
        
        apiCallInProgress = true;
        console.log(`[OAuthCallback-${requestId}] Attempting OAuth callback with:`, { provider, code: code?.substring(0, 20) + '...', state: state?.substring(0, 20) + '...' });
        
        const result = await integrationService.handleOAuthCallback(provider, code, state);
        console.log(`[OAuthCallback-${requestId}] OAuth callback successful:`, result);
        
        apiCallInProgress = false;
        
        // Success - but don't close the popup automatically
        setProcessing(false);
        
        // User must manually close the window
        // This allows them to see any console errors or success messages
      } catch (err: any) {
        apiCallInProgress = false; // Reset flag on error
        
        console.error(`[OAuthCallback-${requestId}] OAuth callback error - Full details:`, {
          error: err,
          response: err.response,
          responseData: err.response?.data,
          responseStatus: err.response?.status,
          responseHeaders: err.response?.headers,
          requestUrl: err.config?.url,
          requestData: err.config?.data
        });
        
        // Handle nested error object structure
        let errorMessage = 'Failed to complete authentication';
        let errorDetails = '';
        
        if (err.response?.data?.error) {
          if (typeof err.response.data.error === 'object') {
            errorMessage = err.response.data.error.message || errorMessage;
            errorDetails = `Code: ${err.response.data.error.code || 'Unknown'}`;
          } else {
            errorMessage = err.response.data.error;
          }
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        // Add response status if available
        if (err.response?.status) {
          errorDetails += ` | Status: ${err.response.status}`;
        }
        
        // Add request URL for debugging
        if (err.config?.url) {
          errorDetails += `\nEndpoint: ${err.config.url}`;
        }
        
        // Combine message and details
        const fullError = errorDetails ? `${errorMessage}\n${errorDetails}` : errorMessage;
        setError(fullError);
        setProcessing(false);
        
        // Don't auto-close on error - let user see the error
        // User can manually close the window
        return; // Ensure we exit here and don't continue
      }
    };

    handleCallback();
  }, [searchParams, navigate, callbackExecuted]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 24,
      }}
    >
      {processing ? (
        <>
          <CircularProgress size={48} style={{ marginBottom: 16 }} />
          <Typography variant="h6" gutterBottom>
            Completing authentication...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we connect your account.
          </Typography>
        </>
      ) : error ? (
        <>
          <Alert severity="error" style={{ marginBottom: 16, maxWidth: 500, whiteSpace: 'pre-wrap' }}>
            {error}
          </Alert>
          <div style={{ marginTop: 16 }}>
            <Typography variant="body2" color="text.secondary" style={{ marginBottom: 16 }}>
              Please copy this error if you need support, then close this window.
            </Typography>
            <button 
              onClick={() => window.close()}
              style={{
                padding: '8px 16px',
                backgroundColor: '#325BA8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Close Window
            </button>
          </div>
        </>
      ) : (
        <>
          <Alert severity="success" style={{ marginBottom: 16, maxWidth: 500 }}>
            Successfully connected!
          </Alert>
          <Typography variant="body2" color="text.secondary" style={{ marginBottom: 16 }}>
            Authentication completed successfully. You can now close this window.
          </Typography>
          <button 
            onClick={() => window.close()}
            style={{
              padding: '8px 16px',
              backgroundColor: '#4DC4D2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Close Window
          </button>
        </>
      )}
    </div>
  );
};

export default OAuthCallback;